import { useConfirmDialog } from '../../hooks/useConfirmDialog';
/**
 * Sezione Siti di Consegna - Solo per Buyer
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  MapPin, 
  Plus, 
  Pencil, 
  Trash2, 
  Star, 
  Building2,
  Loader2,
  PlayCircle,
  Navigation,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DeliverySitesSection = ({ 
  user, 
  t,
  startTrial,
  isExtensionActive
}) => {
  const { confirm: confirmAction, ConfirmDialog } = useConfirmDialog();
  const [hasMultisito, setHasMultisito] = useState(false);
  const [deliverySites, setDeliverySites] = useState([]);
  const [showSiteDialog, setShowSiteDialog] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [siteForm, setSiteForm] = useState({
    name: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    delivery_notes: '',
    is_default: false
  });
  const [savingSite, setSavingSite] = useState(false);
  const [extensionActionLoading, setExtensionActionLoading] = useState(null);
  const [geocodingId, setGeocodingId] = useState(null);

  useEffect(() => {
    if (user?.role === 'buyer') {
      fetchDeliverySites();
    }
  }, [user]);

  const fetchDeliverySites = async () => {
    try {
      const res = await axios.get(`${API}/delivery-sites`);
      setDeliverySites(res.data.sites || []);
      setHasMultisito(res.data.has_multisito || false);
    } catch (error) {
      console.error('Error fetching delivery sites:', error);
    }
  };

  const openSiteDialog = (site = null) => {
    if (site) {
      setEditingSite(site);
      setSiteForm({
        name: site.name || '',
        address: site.address || '',
        city: site.city || '',
        province: site.province || '',
        postal_code: site.postal_code || '',
        contact_name: site.contact_name || '',
        contact_phone: site.contact_phone || '',
        contact_email: site.contact_email || '',
        delivery_notes: site.delivery_notes || '',
        is_default: site.is_default || false
      });
    } else {
      setEditingSite(null);
      setSiteForm({
        name: '',
        address: '',
        city: '',
        province: '',
        postal_code: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        delivery_notes: '',
        is_default: false
      });
    }
    setShowSiteDialog(true);
  };

  const handleSaveSite = async () => {
    if (!siteForm.name || !siteForm.address || !siteForm.city || !siteForm.province || !siteForm.postal_code) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    try {
      setSavingSite(true);
      if (editingSite) {
        await axios.put(`${API}/delivery-sites/${editingSite.id}`, siteForm);
        toast.success('Sito aggiornato');
      } else {
        await axios.post(`${API}/delivery-sites`, siteForm);
        toast.success('Sito aggiunto');
      }
      setShowSiteDialog(false);
      fetchDeliverySites();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSavingSite(false);
    }
  };

  const handleDeleteSite = async (siteId: string) => {
    if (!await confirmAction({ title: 'Elimina sito', description: 'Eliminare questo sito di consegna?', confirmText: 'Elimina', variant: 'destructive' })) return;
    
    try {
      await axios.delete(`${API}/delivery-sites/${siteId}`);
      toast.success('Sito eliminato');
      fetchDeliverySites();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore nell\'eliminazione'));
    }
  };

  const handleSetDefaultSite = async (siteId: string) => {
    try {
      await axios.post(`${API}/delivery-sites/${siteId}/set-default`);
      toast.success('Sito impostato come predefinito');
      fetchDeliverySites();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore'));
    }
  };

  const handleActivateMultisito = async () => {
    setExtensionActionLoading('ext_multisito');
    const result = await startTrial('ext_multisito');
    setExtensionActionLoading(null);
    if (result.success) {
      toast.success('Multi-Sito attivato! Hai 14 giorni di prova gratuita.');
      fetchDeliverySites();
    } else {
      toast.error(result.error || 'Errore nell\'attivazione');
    }
  };

  const handleGeocodeSite = async (siteId) => {
    setGeocodingId(siteId);
    try {
      const res = await axios.post(`${API}/delivery-sites/${siteId}/geocode`);
      toast.success(`📍 Coordinate trovate: ${res.data.latitude.toFixed(4)}, ${res.data.longitude.toFixed(4)}`);
      fetchDeliverySites();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Impossibile trovare le coordinate'));
    } finally {
      setGeocodingId(null);
    }
  };

  const handleGeocodeAll = async () => {
    setGeocodingId('all');
    try {
      const res = await axios.post(`${API}/delivery-sites/geocode-all`);
      if (res.data.geocoded > 0) {
        toast.success(`📍 ${res.data.geocoded} siti geocodificati con successo!`);
        fetchDeliverySites();
      } else {
        toast.info('Tutti i siti hanno già le coordinate');
      }
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore nel geocoding'));
    } finally {
      setGeocodingId(null);
    }
  };

  // Conta siti senza coordinate
  const sitesWithoutCoords = deliverySites.filter(s => !s.latitude || !s.longitude).length;

  if (user?.role !== 'buyer') return null;

  return (
    <>
      <Card data-testid="delivery-sites-section">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Siti di Consegna
            {hasMultisito && (
              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                Multi-Sito Attivo
              </span>
            )}
          </CardTitle>
          <CardDescription>
            {hasMultisito 
              ? 'Gestisci i tuoi punti di consegna per gli ordini'
              : 'Il tuo indirizzo di consegna principale'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Lista Siti */}
          {deliverySites.map(site => (
            <div 
              key={site.id} 
              className={`p-4 rounded-lg border ${site.is_default ? 'border-primary bg-primary/5' : 'bg-muted/30'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{site.name}</span>
                    {site.is_default && (
                      <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                        Predefinito
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {site.address}, {site.postal_code} {site.city} ({site.province})
                  </p>
                  {(site.contact_name || site.contact_phone) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {site.contact_name && `Ref: ${site.contact_name}`}
                      {site.contact_phone && ` • ${site.contact_phone}`}
                    </p>
                  )}
                  {/* Coordinate GPS */}
                  {site.latitude && site.longitude ? (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                      <Navigation className="h-3 w-3" />
                      GPS: {site.latitude.toFixed(4)}, {site.longitude.toFixed(4)}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      Coordinate mancanti
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-amber-600 hover:text-amber-700"
                        onClick={() => handleGeocodeSite(site.id)}
                        disabled={geocodingId === site.id}
                      >
                        {geocodingId === site.id ? (
                          <Loader2 className="h-3 w-3 animate-spin ml-1" />
                        ) : (
                          '- Trova coordinate'
                        )}
                      </Button>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!site.is_default && deliverySites.length > 1 && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleSetDefaultSite(site.id)}
                      title="Imposta come predefinito"
                      data-testid={`set-default-site-${site.id}`}
                    >
                      <Star className="h-4 w-4" />
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => openSiteDialog(site)}
                    data-testid="edit-site-btn"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {deliverySites.length > 1 && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDeleteSite(site.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-site-${site.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Pulsante Geocodifica tutti (se ci sono siti senza coordinate) */}
          {sitesWithoutCoords > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleGeocodeAll}
              disabled={geocodingId === 'all'}
              className="w-full"
            >
              {geocodingId === 'all' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Geocodifica in corso...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Trova coordinate per {sitesWithoutCoords} {sitesWithoutCoords === 1 ? 'sito' : 'siti'}
                </>
              )}
            </Button>
          )}

          {/* Pulsante Aggiungi */}
          {(hasMultisito || deliverySites.length === 0) && (
            <Button 
              variant="outline" 
              onClick={() => openSiteDialog()}
              className="w-full"
              data-testid="add-site-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Aggiungi Sito di Consegna
            </Button>
          )}

          {/* Messaggio per attivare estensione */}
          {!hasMultisito && deliverySites.length >= 1 && (
            <div className="p-4 rounded-lg bg-linear-to-r from-primary/5 to-primary/10 border border-primary/20">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-primary/10">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground">Gestisci più sedi</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Hai più ristoranti o punti vendita? Attiva Multi-Sito per gestire ordini da diverse sedi con un unico account.
                  </p>
                  <Button 
                    className="mt-3"
                    onClick={handleActivateMultisito}
                    disabled={extensionActionLoading === 'ext_multisito'}
                    data-testid="activate-multisito-btn"
                  >
                    {extensionActionLoading === 'ext_multisito' ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <PlayCircle className="h-4 w-4 mr-2" />
                    )}
                    Prova gratis 14 giorni
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Modifica/Aggiungi Sito */}
      <Dialog open={showSiteDialog} onOpenChange={setShowSiteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingSite ? 'Modifica Sito' : 'Nuovo Sito di Consegna'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome Sito *</Label>
              <Input 
                value={siteForm.name}
                onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                placeholder="Es: Sede Centrale, Ristorante Centro..."
                data-testid="site-name-input"
              />
            </div>
            <div>
              <Label>Indirizzo *</Label>
              <Input 
                value={siteForm.address}
                onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })}
                placeholder="Via, numero civico"
                data-testid="site-address-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>CAP *</Label>
                <Input 
                  value={siteForm.postal_code}
                  onChange={(e) => setSiteForm({ ...siteForm, postal_code: e.target.value })}
                  placeholder="20100"
                  data-testid="site-cap-input"
                />
              </div>
              <div>
                <Label>Città *</Label>
                <Input 
                  value={siteForm.city}
                  onChange={(e) => setSiteForm({ ...siteForm, city: e.target.value })}
                  placeholder="Milano"
                  data-testid="site-city-input"
                />
              </div>
              <div>
                <Label>Provincia *</Label>
                <Input 
                  value={siteForm.province}
                  onChange={(e) => setSiteForm({ ...siteForm, province: e.target.value.toUpperCase() })}
                  placeholder="MI"
                  maxLength={2}
                  data-testid="site-province-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Referente</Label>
                <Input 
                  value={siteForm.contact_name}
                  onChange={(e) => setSiteForm({ ...siteForm, contact_name: e.target.value })}
                  placeholder="Nome referente"
                  data-testid="site-contact-name-input"
                />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input 
                  value={siteForm.contact_phone}
                  onChange={(e) => setSiteForm({ ...siteForm, contact_phone: e.target.value })}
                  placeholder="+39..."
                  data-testid="site-contact-phone-input"
                />
              </div>
            </div>
            <div>
              <Label>Note consegna</Label>
              <Textarea 
                value={siteForm.delivery_notes}
                onChange={(e) => setSiteForm({ ...siteForm, delivery_notes: e.target.value })}
                placeholder="Istruzioni per il corriere..."
                rows={2}
                data-testid="site-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSiteDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveSite} disabled={savingSite} data-testid="save-site-btn">
              {savingSite ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                editingSite ? 'Aggiorna' : 'Aggiungi'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeliverySitesSection;
